import {DestroyRef, inject, Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, combineLatestWith, Observable, Subject} from 'rxjs';
import {map, take, tap} from 'rxjs/operators';

import {TreeNode} from '@ali-hm/angular-tree-component';
import {MenuStoreService} from './menu-store.service';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import * as R from 'ramda';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';

@Injectable({providedIn: 'root'})
export class MenuFormFacadeService {

    isLoading$ = this.menuStore.isLoading$.submenus.pipe(
        combineLatestWith([
            this.menuStore.isLoading$.itemChildren,
            this.menuStore.isLoading$.items,
            this.menuStore.isLoading$.itemDetail,
        ]),
        map((sources: [boolean, Observable<boolean>]) => R.any(R.equals(true), sources))
    );
    menuParamsContext$ = this.menuStore.queryConsumers.menuParamsContext$;
    selectedItemDetail$ = this.menuStore.queryItemDetail.selected$;
    isSubMenuRoot$ = this.selectedItemDetail$.pipe(
        map((item) => item.jsonPath),
        map((path) => !path.includes('items'))
    );
    selectTreeNode = new Subject<TreeNode>();
    selectedTreeNode$ = this.selectTreeNode.asObservable();
    updateMenuItem$ = this.menuStore.updateMenuItem$;
    selectedFeatureScope$ = this.menuStore.queryFeatureScope.selected$;
    scope;
    newUpdateFlow$ = this.launchDarklyService.getBooleanFlag(FlagKeys.APPLY_FORCE_OVERRIDE_PER_ATTRIBUTE);

    showCommonItems$ = combineLatest([
        this.launchDarklyService.getBooleanFlag(FlagKeys.USE_COMMON_ITEMS),
        this.selectedItemDetail$
    ]).pipe(
        map(([flagEnabled, selectedItem]) =>
            flagEnabled && selectedItem?.commonItem === true
        )
    );

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(
        private menuStore: MenuStoreService,
        private sharedFacade: SharedFacadeService,
        private launchDarklyService: LaunchDarklyService,
    ) {}

    updateItem(item: ItemDetail, updates: any, dispatchReload: boolean, forceOverrideInheritance?: boolean,
               forceOverrideInheritanceMap?: any): void {
        this.selectedFeatureScope$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                this.scope = result;
            });
        combineLatest([this.sharedFacade.localeSelected$, this.newUpdateFlow$])
            .pipe(
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([{language}, useNewUpdate]) => {
                const localeSupport = item.attributes.reduce((result, itemAttribute) => {
                    result[itemAttribute.jsonProperty] = itemAttribute['localeSupport'];
                    return result;
                }, {});

                let updatesWithLanguage = {};
                if (language !== 'en') {
                    Object.keys(updates).forEach(key => {
                        if (localeSupport[key]) {
                            updatesWithLanguage[`${key}|${language}`] = updates[key];
                        } else {
                            updatesWithLanguage[`${key}`] = updates[key];
                        }
                    });
                } else {
                    updatesWithLanguage = updates;
                }

                useNewUpdate ?
                    this.menuStore.dispatch.updateMenuItem(item, updatesWithLanguage, language, dispatchReload,
                        null, forceOverrideInheritanceMap) :
                    this.menuStore.dispatch.updateMenuItem(item, updatesWithLanguage, language, dispatchReload,
                        forceOverrideInheritance);
            });
    }

    addLocalAttribute(attributeIndex: number): void {
        this.menuStore.dispatch.addLocalAttribute(attributeIndex);
    }

    removeLocalAttribute(attributeIndex: number): void {
        this.menuStore.dispatch.removeLocalAttribute(attributeIndex);
    }

    updateMenuItemCascade(path: string, cascadePath: string | object, updates: any, forceOverrideInheritance: boolean,
                          forceOverrideInheritanceMap?: any): void {
        combineLatest([this.newUpdateFlow$])
            .pipe(
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([useNewUpdate]) => {
                useNewUpdate ? this.menuStore.dispatch.updateMenuItemCascade(path, cascadePath, updates,
                        null, forceOverrideInheritanceMap) :
                    this.menuStore.dispatch.updateMenuItemCascade(path, cascadePath, updates, forceOverrideInheritance);
            });
    }

    filterByAttributeGroup(filterAttributeGroup: string) {
        const filterByGroup$ = new BehaviorSubject<any>(null);
        this.selectedItemDetail$
            .pipe(
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(itemDetail => {
                let filtered;
                let writableAttributes;
                // itemDetail is not writable because it is frozen state from NGRX
                writableAttributes = itemDetail?.attributes.map(attribute => ({...attribute}));

                if (!itemDetail || !writableAttributes) {
                    return;
                }
                if (itemDetail?.fieldRules && writableAttributes) {
                    const attributeMap = new Map(
                        (writableAttributes ?? []).map(attr => [attr.jsonProperty, attr])
                    );

                    Object.entries(itemDetail?.fieldRules)?.forEach(([key, fieldRules]) => {
                        const attributeWithFieldRule = attributeMap.get(key);
                        if (typeof fieldRules === 'object') {
                            if (attributeWithFieldRule) {
                                Object.assign(attributeWithFieldRule, fieldRules);
                            }
                        }
                    });
                }
                filtered = writableAttributes?.filter(attribute => attribute.attributeGroup === filterAttributeGroup);
                // Filter out 'components' attribute from all groups
                filtered = filtered?.filter(attribute => attribute.jsonProperty !== 'components');
                filterByGroup$.next(filtered);
            });
        return filterByGroup$
    }
}
